import { Injectable, inject } from '@angular/core'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import * as Sentry from '@sentry/angular'
import { produce } from 'immer'
import { last as _last } from 'lodash/fp'

import {
  EMPTY,
  Observable,
  catchError,
  filter,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs'

import { ConversationApiService } from '@flowx/designer/modules/ai/common/api/conversation-api.service'
import {
  ActionTarget,
  AddThreadMessageResponse,
  ContextData,
  ContextItem,
  ConversationThread,
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionState,
  ExecutionPlanAction,
  Message,
  PatchPlanActionRequest,
  PatchPlanActionResponse,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AiConversationContextBuilderService } from '@flowx/designer/modules/ai/conversation/ai-conversation-context-builder.service'
import { AiConversationStreamService } from '@flowx/designer/modules/ai/conversation/ai-conversation-stream.service'
import { AgentName } from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { ExecutionPlanActionBase } from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import {
  ContextDataKeys,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { getFileContextKey } from '@flowx/designer/modules/ai/conversation/conversation.utils'
import { AiConversationServiceActions } from '@flowx/designer/modules/ai/store/ai.actions'

import { ConversationErrorActions } from './conversation-error.actions'
import { ExecutionPlanActions } from './execution-plan.actions'

import { UploadContextFile } from '../types/ai.types'

type RunActionContext = {
  action: Pick<
    ExecutionPlanAction,
    'localId' | 'agent' | 'name' | 'description' | 'target' | 'context'
  >
  mergedTarget: ActionTarget
  contextData: ContextData
  basePayload: PatchPlanActionRequest
  conversationId: string
  defaultThread: ConversationThread
  messageWithPlan: Message
  hasDestination: boolean
  target?: { nodeId: number; navigationAreaId?: string }
}

type RunActionState = {
  thread: ConversationThread | null
  lastMessage: Message | null
}

@Injectable({
  providedIn: 'root',
})
export class AiExecutionPlanService {
  #store = inject(Store)
  #actions$ = inject(Actions)
  #conversationApiService = inject(ConversationApiService)
  #aiConversationStreamService = inject(AiConversationStreamService)
  #aiConversationContextBuilderService = inject(AiConversationContextBuilderService)
  #aiConversationService = inject(AiConversationService)

  deleteExecutionPlan(): Observable<unknown> {
    return this.#aiConversationService
      .closeConversation(this.#store.selectSignal(ConversationSelectors.activeConversationId)())
      .pipe(
        tap(() => {
          this.#store.dispatch(ExecutionPlanActions.deleteExecutionPlan())
        }),
        catchError((error) => {
          this.#store.dispatch(ExecutionPlanActions.deleteExecutionPlan())
          console.error('[Conversation Error] Failed to delete execution plan')
          Sentry.captureException(error, {
            tags: { context: 'ai-execution-plan.deleteExecutionPlan' },
          })
          return of(undefined)
        })
      )
  }

  setExecutionPlanActionSource(
    action: ExecutionPlanActionBase,
    file: UploadContextFile,
    fileType: 'image' | 'bpmn'
  ): Observable<any> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const defaultThread = this.#store.selectSignal(ConversationSelectors.defaultThread)()
    const messageWithPlan = this.#store.selectSignal(
      ConversationSelectors.lastDefaultThreadMessageWithValidPlan
    )()

    const sourceContextItem: ContextItem = {
      key: getFileContextKey(action.agent.agent as AgentName),
      itemType: ConversationThreadContextDataItemType.BinaryObject,
      valueBinaryObject: {
        tempFileId: file.id,
        fileName: file.name,
        mimeType: file.type,
      },
    }
    const context: ContextData = {
      items: [
        ...action.context.items.filter(
          ({ itemType }) => itemType !== ConversationThreadContextDataItemType.BinaryObject
        ),
        sourceContextItem,
        {
          key: ContextDataKeys.ProcessReferenceImageType,
          itemType: ConversationThreadContextDataItemType.GenericString,
          valueGenericString: fileType,
        },
      ],
    }

    const payload: PatchPlanActionRequest = {
      state: action.state,
      context,
      target: action.target,
    }
    return this.#conversationApiService
      .patchPlanAction(
        conversationId,
        defaultThread.id,
        messageWithPlan.id,
        action.localId,
        payload
      )
      .pipe(
        tap(() => {
          this.#store.dispatch(
            ExecutionPlanActions.patchExecutionPlanAction({
              actionId: action.localId,
              actionUpdate: {
                context,
              },
            })
          )
        }),
        catchError((error) => {
          console.error('[Conversation Error] Failed to set execution plan action source:', error)
          Sentry.captureException(error, {
            tags: { context: 'ai-execution-plan.setExecutionPlanActionSource' },
          })
          this.#store.dispatch(
            ConversationErrorActions.actionPatchFailed({
              error,
              actionId: action.localId,
              messageId: messageWithPlan.id,
              conversationId,
            })
          )
          return EMPTY
        })
      )
  }

  clearExecutionPlanActionSource(action: ExecutionPlanActionBase): Observable<any> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const defaultThread = this.#store.selectSignal(ConversationSelectors.defaultThread)()
    const messageWithPlan = this.#store.selectSignal(
      ConversationSelectors.lastDefaultThreadMessageWithValidPlan
    )()

    const context: ContextData = {
      items: action.context.items.filter(
        ({ key }) =>
          ![
            ContextDataKeys.ProcessReferenceImage,
            ContextDataKeys.DesignerReferenceImage,
            ContextDataKeys.ProcessReferenceImageType,
          ].includes(key as ContextDataKeys)
      ),
    }

    const payload: PatchPlanActionRequest = {
      state: action.state,
      context,
      target: action.target,
    }

    return this.#conversationApiService
      .patchPlanAction(
        conversationId,
        defaultThread.id,
        messageWithPlan.id,
        action.localId,
        payload
      )
      .pipe(
        tap(() => {
          this.#store.dispatch(
            ExecutionPlanActions.patchExecutionPlanAction({
              actionId: action.localId,
              actionUpdate: {
                context,
              },
            })
          )
        }),
        catchError((error) => {
          console.error('[Conversation Error] Failed to clear execution plan action source:', error)
          Sentry.captureException(error, {
            tags: { context: 'ai-execution-plan.clearExecutionPlanActionSource' },
          })
          this.#store.dispatch(
            ConversationErrorActions.actionPatchFailed({
              error,
              actionId: action.localId,
              messageId: messageWithPlan.id,
              conversationId,
            })
          )
          return EMPTY
        })
      )
  }

  runExecutionPlanAction(
    action: Pick<
      ExecutionPlanAction,
      'localId' | 'agent' | 'name' | 'description' | 'target' | 'context'
    >,
    hasDestination: boolean,
    target?: { nodeId: number; navigationAreaId?: string }
  ): Observable<void> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const defaultThread = this.#store.selectSignal(ConversationSelectors.defaultThread)()

    if (!conversationId) {
      throw new Error('No active conversation found')
    }

    if (!defaultThread || !defaultThread.messages.length) {
      throw new Error('No default thread found or thread has no messages')
    }

    const messageWithPlan = this.#store.selectSignal(
      ConversationSelectors.lastDefaultThreadMessageWithValidPlan
    )()

    const mergedTarget = produce(action.target, (draft) => {
      if (draft.node) {
        draft.node.navigationAreaId = target.navigationAreaId
      }
    })

    return this.#aiConversationContextBuilderService.buildIntentUnderstandingMessageContext().pipe(
      switchMap((contextData) => {
        const ctx: RunActionContext = {
          action,
          mergedTarget,
          contextData,
          basePayload: {
            state: ConversationThreadExecutionPlanActionState.Executing,
            context: { items: contextData.items },
            target: mergedTarget,
          },
          conversationId,
          defaultThread,
          messageWithPlan,
          hasDestination,
          target,
        }
        // `state` is mutated by the initial patch pipeline and read by the
        // confirmation pipeline — the two streams coordinate via this object.
        const state: RunActionState = { thread: null, lastMessage: null }

        const result$ = this.#awaitConfirmationAndStream(ctx, state)
        const subscription = result$.subscribe()

        this.#initiateImplementAction(ctx, state).subscribe()

        return result$.pipe(finalize(() => subscription.unsubscribe()))
      })
    )
  }

  patchExecutionPlanAction(
    actionId: string,
    data: {
      target?: ActionTarget
      state?: ConversationThreadExecutionPlanActionState
    }
  ): Observable<PatchPlanActionResponse> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const defaultThread = this.#store.selectSignal(ConversationSelectors.defaultThread)()

    if (!conversationId) {
      throw new Error('No active conversation found')
    }

    if (!defaultThread || !defaultThread.messages.length) {
      throw new Error('No default thread found or thread has no messages')
    }

    const messageWithPlan = this.#store.selectSignal(
      ConversationSelectors.lastDefaultThreadMessageWithValidPlan
    )()

    const action = messageWithPlan.executionPlan.actions.find(
      (action) => action.localId === actionId
    )
    if (!action) {
      throw new Error('Action not found')
    }

    const payload: PatchPlanActionRequest = {
      state: data.state ?? action.state,
      target: data.target ?? action.target,
      context: { items: [] },
    }

    return this.#conversationApiService
      .patchPlanAction(conversationId, defaultThread.id, messageWithPlan.id, actionId, payload)
      .pipe(
        tap(() => {
          this.#store.dispatch(
            ExecutionPlanActions.patchExecutionPlanActionState({
              actionId,
              actionState: data.state ?? action.state,
              messageId: messageWithPlan.id,
            })
          )
        })
      )
  }

  #awaitConfirmationAndStream(ctx: RunActionContext, state: RunActionState): Observable<void> {
    const {
      action,
      mergedTarget,
      contextData,
      basePayload,
      conversationId,
      defaultThread,
      messageWithPlan,
    } = ctx

    return this.#actions$.pipe(
      ofType(ExecutionPlanActions.runExecutionPlanConfirmation),
      filter((eventAction) => eventAction.actionId === action.localId),
      take(1),
      switchMap((actionData) => this.#addImplementActionMessage(actionData, action, mergedTarget)),
      switchMap((actionData) =>
        this.#conversationApiService
          .patchPlanAction(conversationId, defaultThread.id, messageWithPlan.id, action.localId, {
            ...basePayload,
            target: actionData.target,
            context: {
              items: this.#aiConversationContextBuilderService.deduplicateContextItems([
                ...(actionData.context?.items ?? []),
                ...(action.context?.items ?? []),
                ...(contextData.items ?? []),
              ]),
            },
          })
          .pipe(map(() => actionData))
      ),
      tap((actionData) => {
        this.#store.dispatch(
          ExecutionPlanActions.patchExecutionPlanActionTargetSuccess({
            actionId: action.localId,
            target: actionData.target,
          })
        )
        this.#store.dispatch(
          ConversationActions.updateMessageContextData({
            messageId: state.lastMessage.id,
            contextData: (actionData.context?.items ?? []) as unknown as ContextItem[],
          })
        )
        this.#store.dispatch(AiConversationServiceActions.executionPlanActionListenStarted())

        if (state.lastMessage?.id) {
          this.#aiConversationStreamService
            .listenForMessageUpdates({
              conversationId,
              threadId: state.thread.id,
              messageId: state.lastMessage.id,
            })
            .subscribe()
        }
      }),
      catchError((error: any) => {
        Sentry.captureException(error, {
          tags: { context: 'ai-execution-plan.runExecutionPlanAction.implement' },
        })
        this.#store.dispatch(
          ConversationErrorActions.executionPlanImplementActionFailed({
            error,
            actionId: action.localId,
            messageId: state.lastMessage.id,
            conversationId,
            threadId: state.thread.id,
          })
        )
        return EMPTY
      }),
      map(() => undefined),
      shareReplay({ bufferSize: 1, refCount: true })
    )
  }

  #initiateImplementAction(ctx: RunActionContext, state: RunActionState): Observable<void> {
    const {
      action,
      mergedTarget,
      contextData,
      basePayload,
      conversationId,
      defaultThread,
      messageWithPlan,
      hasDestination,
      target,
    } = ctx

    return this.#conversationApiService
      .patchPlanAction(conversationId, defaultThread.id, messageWithPlan.id, action.localId, {
        ...basePayload,
        context: {
          items: this.#aiConversationContextBuilderService.deduplicateContextItems([
            ...(action.context?.items ?? []),
            ...(contextData?.items ?? []),
          ]),
        },
      })
      .pipe(
        take(1),
        tap((response) => {
          if (!response?.child) {
            return
          }

          state.thread = {
            ...response.child.thread,
            messages: [response.child.seedMessage],
          }
          state.lastMessage = this.#updateLastMessage(state.thread, target, action, mergedTarget)

          this.#store.dispatch(
            ExecutionPlanActions.patchExecutionPlanActionApiResponse({
              thread: state.thread,
              conversationId,
            })
          )
          this.#store.dispatch(
            ExecutionPlanActions.patchExecutionPlanActionState({
              actionId: action.localId,
              actionState: ConversationThreadExecutionPlanActionState.Executing,
              messageId: messageWithPlan.id,
            })
          )

          if (hasDestination) {
            this.#store.dispatch(
              ExecutionPlanActions.runExecutionPlanConfirmation({
                actionId: action.localId,
                target: mergedTarget,
              })
            )
          }
        }),
        switchMap(() => {
          if (hasDestination) {
            return of(null)
          }

          return this.#addDestinationSelectorMessage(action, mergedTarget, target, contextData)
        }),
        catchError((error: any) => {
          console.error('[Conversation Error] Failed to run execution plan action:', error)
          Sentry.captureException(error, {
            tags: { context: 'ai-execution-plan.runExecutionPlanAction' },
          })
          this.#store.dispatch(
            ConversationErrorActions.executionPlanImplementActionFailed({
              error,
              actionId: action.localId,
              messageId: messageWithPlan.id,
              conversationId,
              threadId: defaultThread.id,
            })
          )
          return EMPTY
        }),
        map(() => undefined)
      )
  }

  #updateLastMessage(
    thread: ConversationThread,
    target: { nodeId: number; navigationAreaId?: string },
    action: Pick<
      ExecutionPlanAction,
      'localId' | 'agent' | 'name' | 'description' | 'target' | 'context'
    >,
    mergedTarget: ActionTarget
  ): Message | null {
    const lastMessage = _last(thread.messages)
    if (!lastMessage) {
      return null
    }

    const targetItem = lastMessage.contextData.items.find(
      (item) => item.key === ContextDataKeys.UiTarget
    )
    const newTarget: ContextItem = {
      key: ContextDataKeys.UiTarget,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify({
        ...JSON.parse(targetItem?.valueGenericString ?? '{}'),
        ...(target ?? {}),
      }),
    }

    lastMessage.contextData.items = [
      ...lastMessage.contextData.items.filter((item) => item.key !== ContextDataKeys.UiTarget),
      {
        key: ContextDataKeys.UIMessageType,
        itemType: ConversationThreadContextDataItemType.GenericString,
        valueGenericString: MessageType.UserMsg,
      },
      newTarget,
    ]
    lastMessage.executionPlan = {
      ...lastMessage.executionPlan,
      actions: [
        {
          ...lastMessage.executionPlan.actions[0],
          target: mergedTarget,
        },
        ...lastMessage.executionPlan.actions.slice(1),
      ],
    }

    return lastMessage
  }

  #addDestinationSelectorMessage(
    action: Pick<
      ExecutionPlanAction,
      'localId' | 'agent' | 'name' | 'description' | 'target' | 'context'
    >,
    mergedTarget: ActionTarget,
    target: { nodeId: number; navigationAreaId?: string },
    contextData: ContextData
  ): Observable<AddThreadMessageResponse> {
    let message = ''
    if (action.agent.agent === AgentName.AiDesigner) {
      message =
        'Where would you like to implement this UI? I need your help identifying the user task in which to apply it.'
    } else if (action.agent.agent === AgentName.AiDeveloper) {
      message =
        'Where would you like to implement this business rule? I need your help identifying the node and action.'
    }

    return this.#aiConversationService.addStoreMessage(
      {
        prompt: message,
        type: MessageType.SystemMsg,
      },
      [
        ...contextData.items,
        {
          key: ContextDataKeys.UiDestinationSelector,
          itemType: ConversationThreadContextDataItemType.GenericString,
          valueGenericString: JSON.stringify({
            agent: action.agent.agent,
            action: action.agent.action,
            actionId: action.localId,
            type: mergedTarget?.expression?.expressionType,
            nodeId: target?.nodeId ?? '',
          }),
        },
      ],
      mergedTarget
    )
  }

  #addImplementActionMessage(
    actionData: any,
    action: Pick<
      ExecutionPlanAction,
      'localId' | 'agent' | 'name' | 'description' | 'target' | 'context'
    >,
    mergedTarget: ActionTarget
  ): Observable<any> {
    if (
      action.agent.agent === AgentName.AiAnalyst ||
      action.agent.agent === AgentName.AiAssistant
    ) {
      return of(actionData)
    }

    let message = mergedTarget?.name
    if (!message?.length) {
      const target = JSON.parse(
        actionData.context?.items.find((item) => item.key === ContextDataKeys.UiTarget)
          ?.valueGenericString ?? '{}'
      )
      message = target.name
    }

    if (message?.length) {
      return this.#aiConversationService
        .addStoreMessage(
          {
            prompt: `Implement in ${message}`,
            type: MessageType.UserMsg,
          },
          actionData.context?.items,
          actionData.target
        )
        .pipe(map(() => actionData))
    }

    return of(actionData)
  }
}
